import Overlay from '@/components/molecules/Overlay';
import { PHARMACY_MEDICINE_NAMES_MAP } from '@/constants/pharmacy';
import { useEffect, useRef, useState } from 'react';

interface MedicineInfoBottomSheetProps {
  treatmentMedicineIds: string[];
  onClickClose: () => void;
}

const MedicineInfoBottomSheet = ({ treatmentMedicineIds, onClickClose }: MedicineInfoBottomSheetProps) => {
  const [hasOverflow, setHasOverflow] = useState<Record<number, boolean>>({});
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({});
  const textRefs = useRef<(HTMLParagraphElement | null)[]>([]);

  // 선택된 약 그룹 정보 (없으면 빈 배열)
  const selectedMedicineGroup =
    treatmentMedicineIds.length > 1 && PHARMACY_MEDICINE_NAMES_MAP
      ? treatmentMedicineIds.map((id) => PHARMACY_MEDICINE_NAMES_MAP[id]).filter(Boolean)
      : [];

  // 더보기 핸들러
  const expandItem = (index: number) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: true,
    }));
  };

  // 텍스트 오버플로우 감지
  useEffect(() => {
    if (selectedMedicineGroup.length === 0) return;

    // 요소가 렌더링된 후에 실행
    const checkOverflow = () => {
      const newHasOverflow: Record<number, boolean> = {};

      textRefs.current.forEach((ref, index) => {
        if (ref) {
          // scrollHeight > clientHeight 또는 scrollWidth > clientWidth일 경우 오버플로우 발생
          const isOverflowing = ref.scrollHeight > ref.clientHeight || ref.scrollWidth > ref.clientWidth;
          newHasOverflow[index] = isOverflowing;
        }
      });

      setHasOverflow(newHasOverflow);
    };

    checkOverflow();
    // 윈도우 리사이즈 시 오버플로우 상태 재확인
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [selectedMedicineGroup]);

  const getDisplayedMedicineList = (list: string[]) => {
    if (!list || !list.length) return '';
    return list.join(', ');
  };

  // 약품 목록이 비어있는지 확인
  const isMedicineGroupEmpty = selectedMedicineGroup.length === 0;

  // 내용물 렌더링
  const renderContent = () => {
    // 목록이 비어있는 경우
    if (isMedicineGroupEmpty) {
      return (
        <div className='flex flex-col space-y-8 pb-6'>
          <div className='flex flex-col gap-2 px-1'>
            <div className='flex flex-row items-start gap-2'>
              <div className='mt-[7px] h-1 w-1 rounded-full bg-gray-400'></div>
              <p className='typography-ui-13m text-gray-500'>선택된 약품이 없습니다.</p>
            </div>
          </div>
        </div>
      );
    }

    // 기본 약품 목록 표시
    return (
      <div className='flex flex-col px-[4px] pb-6'>
        {selectedMedicineGroup.map((medicine, index) => (
          <div key={index} className='flex flex-col'>
            <div className='flex flex-col gap-1'>
              <p className='typography-ui-15sb text-gray-900'>{medicine.name}</p>
              <div className='flex flex-row items-center gap-1'>
                <p
                  ref={(el) => {
                    textRefs.current[index] = el;
                  }}
                  className={`typography-ui-13r ${!expandedItems[index] ? 'line-clamp-1' : ''} flex-1 text-gray-600`}
                >
                  {getDisplayedMedicineList(medicine.medicineList)}
                </p>
                {/* 오버플로우가 발생했을 때만 더보기 버튼 표시 */}
                {hasOverflow[index] && !expandedItems[index] && (
                  <button
                    onClick={() => expandItem(index)}
                    className='typography-ui-13r !underline cursor-pointer whitespace-nowrap border-0 bg-transparent text-gray-600 underline-offset-2'
                  >
                    더보기
                  </button>
                )}
              </div>
            </div>
            {/* 마지막 항목이 아닌 경우에만 구분선 표시 */}
            {index < selectedMedicineGroup.length - 1 && <div className='my-4 h-[1px] w-full bg-gray-40'></div>}
          </div>
        ))}

        <div className='mt-4 mb-8 flex flex-col gap-2 px-1'>
          <div className='flex flex-row items-start gap-2'>
            <div className='mt-[7px] h-[4px] min-w-[4px] rounded-full bg-gray-200' />
            <p className='typography-ui-13m text-gray-400'>
              성분 선택은 진료 및 상담 과정에서 의사소통을 원활하게 하기 위한 목적으로 활용됩니다.
            </p>
          </div>
          <div className='flex flex-row items-start gap-2'>
            <div className='mt-[7px] h-[4px] min-w-[4px] rounded-full bg-gray-200' />
            <p className='typography-ui-13m text-gray-400'>
              약품명은 각 성분에 대한 이해를 돕기 위한 표기이며, 약품은 의사의 전문적 판단에 따라 처방됩니다.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // 오버레이 클릭 핸들러
  const handleOverlayClick = () => {
    onClickClose();
  };

  return (
    <Overlay onClickOverlay={handleOverlayClick} className='!items-end' index={500}>
      <div className='relative max-h-[80vh] w-full max-w-full animate-slideUp overflow-y-scroll rounded-t-[24px] bg-white px-[20px] pt-[24px]'>
        <div className='mb-4 flex flex-row items-center justify-between px-[4px]'>
          <p className='typography-ui-20sb text-gray-800'>성분 안내</p>
        </div>
        <div className='max-h-[480px] overflow-y-scroll'>{renderContent()}</div>
        <button
          onClick={onClickClose}
          className='typography-ui-17sb mb-4 flex w-full items-center justify-center rounded-xl bg-primary-500 py-[15px] text-white hover:bg-primary-600'
        >
          확인
        </button>
      </div>
    </Overlay>
  );
};

export default MedicineInfoBottomSheet;
